import {useFluxStore} from 'client/framework';
import classNames from 'classnames';
import React, {useRef, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {Button, Icon, IconButton, LoadingSpinner, WithTooltip} from '@albert-io/atomic';
import {local} from 'lib/StorageUtil';
import NewTranslationToggleTour from './NewTranslationToggleTour';
import {useQuestionTranslationStore} from 'client/components/QuestionTypes/shared/TranslationSection';
import type {ButtonColor} from '@albert-io/atomic/atoms/Button/Button.react';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import sessionStore from 'client/Session/SessionStore';

const LOCAL_STORAGE_KEY_PREFIX = 'new-translation-toggle-tour';

interface Props {
  light?: boolean;
  popoverPlacement?: 'bottom-end' | 'top-end' | 'top-start';
  variant: 'IconButton' | 'Button';
  viewType:
    | 'adaptive'
    | 'assignment-builder'
    | 'legacy-folder'
    | 'student-assignment'
    | 'student-submission'
    | 'subject-practice';
  className?: string;
}

export default function NewTranslationToggle({
  light = false,
  popoverPlacement = 'bottom-end',
  variant,
  viewType,
  className
}: Props) {
  const userId = useFluxStore(sessionStore, (s) => s.getUserId() ?? 'not-logged-in');
  const tourId = `${LOCAL_STORAGE_KEY_PREFIX}-${userId}-${viewType}`;

  const showTour = ['adaptive', 'assignment-builder', 'student-assignment'].includes(viewType);

  const translationToggleRef = useRef(null);

  const [hasDismissedTour, setHasDismissedTour] = useState(local.get(tourId, false));

  function getButtonColor(): ButtonColor {
    if (showTranslations) {
      return 'primary';
    }

    return light ? 'secondary' : 'blend-dark';
  }

  function onDismissTour() {
    local.set(tourId, true);
    setHasDismissedTour(true);
  }

  const {fetchingTranslations, showTranslations, setShowTranslations, translationCoverage} =
    useQuestionTranslationStore(
      useShallow((state) => ({
        fetchingTranslations: state.fetchingTranslations,
        showTranslations: state.showTranslations,
        setShowTranslations: state.onToggle,
        translationCoverage: state.translationCoverage
      }))
    );

  const tooltipEnabled =
    (!showTour || hasDismissedTour) &&
    (['partial', 'none'].includes(translationCoverage) || variant === 'IconButton');

  let tooltipContent = `Turn translations ${showTranslations ? 'off' : 'on'}`;

  if (translationCoverage !== 'full') {
    tooltipContent =
      translationCoverage === 'partial'
        ? 'Partial translations available'
        : 'Unavailable for this question';
  }

  const buttonLabel = `Translate to ${showTranslations ? 'English' : 'Spanish'}`;
  const languageIcon: IconProp = ['fas', 'language'];

  // This renders a slash through the icon when there is no translation coverage as there is no icon for this.
  const iconSlash = (
    <>
      {translationCoverage === 'none' && (
        <div
          className={classNames(
            'absolute! z-10 h-[3px] w-x2-half -rotate-45 rounded-[4px] border border-content-primary-inverse-static background-brand',
            {'bottom-[37.5%]': variant === 'Button'}
          )}
        />
      )}
    </>
  );

  function onClick() {
    if (!hasDismissedTour) {
      onDismissTour();
    }

    setShowTranslations();
  }

  return (
    <>
      {fetchingTranslations ? (
        <LoadingSpinner
          bottomMarginLevel={0}
          className={classNames('items-center', {
            'content-primary-inverse-static': !light,
            'px-x1-half': variant === 'IconButton',
            'px-x10': variant === 'Button'
          })}
        />
      ) : (
        <div>
          <WithTooltip
            className='rounded-2xl'
            content={tooltipContent}
            enabled={tooltipEnabled}
            placement={popoverPlacement}
            tooltipClassName={classNames('border-none', {
              'bg-background-warning!': translationCoverage === 'partial'
            })}
          >
            {variant === 'Button' ? (
              <Button
                className={classNames(
                  'gap-x1-half',
                  {
                    'opacity-100!': translationCoverage === 'none'
                  },
                  className
                )}
                color={getButtonColor()}
                disabled={translationCoverage === 'none'}
                onClick={onClick}
                size='s'
                variant='text'
              >
                <div>
                  <Icon icon={languageIcon} />
                  {iconSlash}
                </div>
                {buttonLabel}
              </Button>
            ) : (
              <IconButton
                label={buttonLabel}
                color={getButtonColor()}
                className={classNames(
                  {
                    'background-primary! hover:background-primary-hover!':
                      showTranslations && !light,
                    'background-brand! hover:background-brand-bold-hover!':
                      showTranslations && light,
                    'opacity-100!': translationCoverage === 'none'
                  },
                  className
                )}
                disabled={translationCoverage === 'none'}
                icon={languageIcon}
                onClick={onClick}
                ref={translationToggleRef}
                variant={light && showTranslations ? 'solid' : undefined}
              >
                {iconSlash}
              </IconButton>
            )}
          </WithTooltip>
          {showTour && !hasDismissedTour && (
            <NewTranslationToggleTour
              onDismissTour={onDismissTour}
              popoverPlacement={popoverPlacement}
              translationToggleRef={translationToggleRef}
            />
          )}
        </div>
      )}
    </>
  );
}
